import { useBase } from '@/utils/request';
import type { GameSingleCfg } from '@/components/SelectGame/type.d';
import { isPlainObject, isFunction } from 'lodash-es';
import { useAuthStageStore } from '@/store/global/auth.store';
import { storeToRefs } from 'pinia';
import type { RequestOptions } from '@/types/index';
import baseinfoByGameMock from '@/mock/baseinfo_by_game.json';
import { useEnv } from '@/utils/env';

const { isUseMockData } = useEnv();

/**
 * 用户操作权限项定义
 * @property action - 操作名称（如"edit"/"view"）
 * @property desc - 操作描述
 * @property dimension - 权限维度配置（支持游戏/权限/媒体渠道等多维度控制）
 */
export type TActionItem = {
  action: string;
  desc: string;
  dimension: {
    [key: string]: string | string[] | number[] | undefined | { [key: string]: string[] };
    game?: string;
    privileges?: string[];
    media_channel?: number[];
    api?: string[];
    accounts?: {
      [key: string]: string[];
    };
  };
};
/**
 * 游戏基础信息接口返回结构
 * @property role - 用户角色列表
 * @property game - 当前游戏标识
 * @property user - 用户名
 * @property allow_routes - 允许访问的路由列表
 * @property allow_games - 允许访问的游戏配置
 * @property actions - 操作权限配置
 */

export type TRspGetBaseInfoByGame = {
  role: Array<string>;
  game: string;
  user: string;
  allow_routes: Array<string>;
  allow_games: Record<string, GameSingleCfg> | undefined;
  actions: Record<string, TActionItem[]>;
};

/**
 * 获取游戏基础信息请求参数
 * @property game - 游戏标识（必填）
 * @property __no_cache - 是否禁用缓存（传'1'禁用）
 */
export type TBaseInfoByGameParams = {
  game: string;
  __no_cache?: string; // 不使用缓存? 就传'1'
};

/**
 * 获取游戏基础信息（带缓存机制）
 * @param params - 请求参数
 * @param opt - 请求配置项
 * @param callbackByRefresh - 当检测到配置变更时的回调函数
 * @returns 用户基础信息
 *
 * 缓存逻辑：
 * 1. 根据参数和用户名生成唯一缓存key
 * 2. 优先读取localStorage缓存
 * 3. 若接口返回数据与缓存不一致，触发回调并更新缓存
 */
export async function getBaseInfoByGame(
  params?: TBaseInfoByGameParams,
  opt?: RequestOptions,
  callbackByRefresh?: Function,
) {
  const { currentUser } = storeToRefs(useAuthStageStore());
  const { setBaseInfo } = useAuthStageStore();
  // 不同的登录态对应了不同的数据，这里不能共用存储
  const key = `baseInfo:${JSON.stringify(params)}-username:${currentUser.value}`;
  const cacheData = localStorage.getItem(key);
  if (cacheData) {
    getBaseInfo(params, opt).then((data) => {
      try {
        if (!isPlainObject(data)) return;
        // 接口返回的数据和缓存的数据不同，说明后台下发的配置发生了变化，再刷新一次路由
        if (cacheData !== JSON.stringify(data)) {
          isFunction(callbackByRefresh) && callbackByRefresh(data);
          setBaseInfo(data); // 接口返回的baseinfo重新设置到store中
        }
        localStorage.setItem(key, JSON.stringify(data));
        localStorage.setItem('imarket-username', data.user);
      } catch (e) {
        localStorage.clear();
      }
    });
    const userInfo = JSON.parse(cacheData);
    setBaseInfo(userInfo);
    return userInfo;
  }
  const data = await getBaseInfo(params);
  try {
    if (isPlainObject(data)) {
      localStorage.setItem(key, JSON.stringify(data));
      localStorage.setItem('imarket-username', data.user);
    }
  } catch (e) {
    localStorage.clear();
  }

  setBaseInfo(data);
  return data;
}
/**
 * 实际请求函数（支持Mock/真实接口切换）
 * @param params - 请求参数
 * @param opt - 请求配置项
 * @returns 基础信息数据
 *
 * Mock模式：
 * 根据isUseMockData环境变量自动切换mock数据源
 */
export function getBaseInfo(params?: any, opt?: RequestOptions) {
  const { get } = useBase(
    params
      ? {
          ...(opt || {}),
          interceptorRequest(config) {
            return {
              ...config,
              params: {
                ...config.params,
                __game: params!.game,
              },
            };
          },
          ...(params?.game ? { getGame: () => params.game } : {}),
        }
      : opt,
  );
  return isUseMockData
    ? Promise.resolve(baseinfoByGameMock.data as any)
    : get<typeof params, TRspGetBaseInfoByGame>('/api/base_info', {
        params: {
          ...params,
          __no_cache: '1',
        },
      });
}
